import { useCallback, useEffect } from 'react';
import { useCollaborationIdentity, usePresenceActions } from './PresenceProvider';

export function useCollaborationPresence() {
  const { enabled } = useCollaborationIdentity();
  const { updatePresence } = usePresenceActions();

  const setSelectedRowId = useCallback((selectedRowId: string | null) => {
    if (!enabled) return;
    updatePresence({ selectedRowId });
  }, [enabled, updatePresence]);

  const setActiveField = useCallback((activeField: string | null) => {
    if (!enabled) return;
    updatePresence({ activeField });
  }, [enabled, updatePresence]);

  const bindCursorTracking = useCallback((element: HTMLElement | null) => {
    if (!enabled || !element) return () => undefined;

    const handleMouseMove = (event: MouseEvent) => {
      const bounds = element.getBoundingClientRect();
      updatePresence({
        cursor: {
          x: event.clientX - bounds.left,
          y: event.clientY - bounds.top,
        },
      });
    };

    const handleMouseLeave = () => {
      updatePresence({ cursor: null });
    };

    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enabled, updatePresence]);

  useEffect(() => {
    if (!enabled) return;
    return () => {
      updatePresence({ cursor: null, selectedRowId: null, activeField: null });
    };
  }, [enabled, updatePresence]);

  return { enabled, setSelectedRowId, setActiveField, bindCursorTracking };
}
